import { useMutation, useQuery } from '@apollo/client';
import React, { useState } from 'react';
import { Button, SuccessButton } from '../connectedForm/Button';
import DateField from '../../components/connectedForm/DateInput/DateInput';
import CheckboxInput from '../../components/connectedForm/CheckboxInput';
import { todaysDate } from '../../utils/DateHelper';
import { useComponentQueryErrorHandling } from '../../utils/parseErrorResponse';
import { SuggestedPayment } from '../../graphql/entities';
import { serviceActions } from '../../state/modules/service';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import getClientMutationId from '../../utils/getClientMutationId';
import { BlurTextField } from '../connectedForm/inputComponents';
import { useValueState } from '../../utils/hooks';
import { AlertType } from '../../graphql/schema/p4types';
import { BasicHeader } from '../../actions/BasicHeader';
import { ModalBottomButtonContainer } from './Styles';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { useActionProps } from '../../utils/useActionProps';
import FluentModal from './FluentModal';

const SuggestedPaymentModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [paymentDate, setPaymentDate] = useState(todaysDate());
  const [includeAllWithSettlementDiscount, setIncludeAllWithSettlementDiscount] = useValueState(false);
  const [daysOverdue, setDaysOverdue] = useState(0);
  const [paymentLimit, setPaymentLimit] = useState(0);

  const handleQueryError = useComponentQueryErrorHandling();
  const [getSuggestedPaymentsMutation] = useMutation(SuggestedPayment.generate!, {
    onError: handleQueryError,
  });

  const { data: defaultRecord } = useQuery(SuggestedPayment.default!);

  const actionProps = useActionProps('supplier');

  const handleGenerate = () => {
    const defaultValues = {
      ...defaultRecord?.defaultRecord,
      paymentDate,
      includeAllWithSettlementDiscount,
      daysOverdue,
      paymentLimit,
    };

    const input = {
      clientMutationId: getClientMutationId(),
      ...normalizeVariables(actionProps),
      suggestedPayment: normalizeVariables(defaultValues),
    };

    getSuggestedPaymentsMutation({ variables: { input } }).then(({ data }) => {
      const generatedResultUuid = data?.suggestedPaymentGeneration?.result?.uuid;

      if (generatedResultUuid) {
        dispatch(
          serviceActions.genericAlert(
            'Payment List Created',
            'Suggested payment list created, navigating to form',
            AlertType.Success
          )
        );

        history.push({
          pathname: `/Suppliers/SuggestedPayment/Details/Temp/${generatedResultUuid}`,
          state: {
            previousLocation: history.location?.pathname || 'back',
          },
        });
      } else {
        dispatch(
          serviceActions.genericAlert(
            'Payment List Not Created',
            'Suggested payment list could not be created.',
            AlertType.Success
          )
        );
      }
    });
  };

  return (
    <FluentModal isOpen={isOpen} styles={{ main: { minWidth: '600px', paddingBottom: '60px' } }}>
      <BasicHeader>Generate Suggested Payments</BasicHeader>
      <div style={{ padding: '1em' }}>
        <DateField
          data-testid="suggested-payment-payment-date"
          label="Payment Date"
          value={paymentDate}
          onChange={setPaymentDate}
          labelCols={1 / 2}
          inputCols={1 / 2}
          portal={true}
        />
        <BlurTextField
          data-testid="suggested-payment-days-overdue"
          label="Days Overdue"
          value={daysOverdue}
          onChange={setDaysOverdue}
          textAlign="right"
          decimals={0}
          labelCols={1 / 2}
          inputCols={1 / 2}
          enableOnChange
        />
        <BlurTextField
          data-testid="suggested-payment-payment-limit"
          label="Payment Limit"
          value={paymentLimit}
          onChange={setPaymentLimit}
          textAlign="right"
          decimals={2}
          labelCols={1 / 2}
          inputCols={1 / 2}
          enableOnChange
        />
        <CheckboxInput
          data-testid="suggested-payment-include-all-with-settlement-discount"
          label="All With Settlement Discount"
          value={includeAllWithSettlementDiscount}
          onChange={setIncludeAllWithSettlementDiscount}
          labelCols={2 / 3}
          inputCols={1 / 3}
        />
      </div>
      <ModalBottomButtonContainer>
        <Button data-testid="paymentModal-cancel" onClick={onClose}>
          Cancel
        </Button>
        <SuccessButton data-testid="paymentModal-auto" onClick={handleGenerate}>
          Generate
        </SuccessButton>
      </ModalBottomButtonContainer>
    </FluentModal>
  );
};

export default SuggestedPaymentModal;
